import { useEffect, useCallback, useRef } from 'react';
import debounce from '../utils/debounce';

/**
 * Threshold in pixels from the top to trigger backward pagination
 */
const SCROLL_THRESHOLD_TOP = 100;

/**
 * Threshold in pixels from the bottom to trigger forward pagination
 */
const SCROLL_THRESHOLD_BOTTOM = 100;

/**
 * Debounce delay for scroll events in milliseconds
 */
const SCROLL_DEBOUNCE_DELAY = 150;

/**
 * Hook for handling scroll-based timeline pagination
 * 
 * @param {Object} options - Configuration options
 * @param {HTMLElement} options.scrollContainer - The scrollable container element
 * @param {boolean} options.canPaginateBackward - Whether backward pagination is available
 * @param {boolean} options.canPaginateForward - Whether forward pagination is available
 * @param {boolean} options.isLoadingOlder - Whether currently loading older messages
 * @param {boolean} options.isLoadingNewer - Whether currently loading newer messages
 * @param {boolean} options.isFillingViewport - Whether currently filling viewport
 * @param {Function} options.onPaginateBackward - Callback to paginate backward
 * @param {Function} options.onPaginateForward - Callback to paginate forward
 * @returns {Object} Scroll pagination utilities
 */
export const useScrollPagination = ({
  scrollContainer,
  canPaginateBackward = false,
  canPaginateForward = false,
  isLoadingOlder = false,
  isLoadingNewer = false,
  isFillingViewport = false,
  onPaginateBackward = () => {},
  onPaginateForward = () => {},
}) => {
  const lastScrollTop = useRef(0);
  const isScrollingToBottom = useRef(false);

  /**
   * Handle scroll events to trigger pagination
   */
  const handleScroll = useCallback(
    debounce(() => {
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;

      // Detect scroll direction
      const isScrollingDown = scrollTop > lastScrollTop.current;
      lastScrollTop.current = scrollTop;

      // Check if user scrolled near the top (load older messages)
      if (
        scrollTop <= SCROLL_THRESHOLD_TOP &&
        !isScrollingDown &&
        canPaginateBackward &&
        !isLoadingOlder &&
        !isFillingViewport
      ) {
        onPaginateBackward();
      }

      // Check if user scrolled near the bottom (load newer messages)
      if (
        scrollBottom <= SCROLL_THRESHOLD_BOTTOM &&
        isScrollingDown &&
        canPaginateForward &&
        !isLoadingNewer &&
        !isFillingViewport
      ) {
        onPaginateForward();
      }
    }, SCROLL_DEBOUNCE_DELAY),
    [
      scrollContainer,
      canPaginateBackward,
      canPaginateForward,
      isLoadingOlder,
      isLoadingNewer,
      isFillingViewport,
      onPaginateBackward,
      onPaginateForward,
    ],
  );

  /**
   * Scroll to bottom of container
   */
  const scrollToBottom = useCallback(() => {
    if (!scrollContainer) return;
    
    isScrollingToBottom.current = true;
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
    
    // Reset flag after a short delay
    setTimeout(() => {
      isScrollingToBottom.current = false;
    }, 100);
  }, [scrollContainer]);

  /**
   * Scroll to top of container
   */
  const scrollToTop = useCallback(() => {
    if (!scrollContainer) return;
    scrollContainer.scrollTop = 0;
  }, [scrollContainer]);

  /**
   * Maintain scroll position when new content is added at the top
   */
  const maintainScrollPosition = useCallback((previousScrollHeight) => {
    if (!scrollContainer || isScrollingToBottom.current) return;
    
    const newScrollHeight = scrollContainer.scrollHeight;
    const heightDifference = newScrollHeight - previousScrollHeight;
    
    if (heightDifference > 0) {
      // Store current scroll position relative to the bottom before adjusting
      const currentScrollTop = scrollContainer.scrollTop;
      
      // Adjust scroll position to maintain visual position
      const newScrollTop = currentScrollTop + heightDifference;
      scrollContainer.scrollTop = newScrollTop;
    }
  }, [scrollContainer]);

  /**
   * Set up scroll event listener
   */
  useEffect(() => {
    if (!scrollContainer) {
      return;
    }

    scrollContainer.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainer, handleScroll]);

  /**
   * Auto-scroll to bottom when new live messages arrive
   * Only if user is already near the bottom
   */
  const autoScrollToNewMessages = useCallback(() => {
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    
    // If user is near the bottom, auto-scroll to show new messages
    if (scrollBottom <= SCROLL_THRESHOLD_BOTTOM * 2) {
      scrollToBottom();
    }
  }, [scrollContainer, scrollToBottom]);

  return {
    scrollToBottom,
    scrollToTop,
    maintainScrollPosition,
    autoScrollToNewMessages,
  };
};

export default useScrollPagination;